/**
 * Google Drive–style avatar stack of the other people looking at this session
 * right now. A user drops off the moment their tab is hidden or they go idle
 * (see useUserActivity), and comes back as soon as they return to it.
 */

import { useEffect, useRef, useState } from 'react';
import { usernameColor } from '@/utils/userColor';

export interface ActiveViewer {
  userName: string;
  /** Has edit rights in at least one of their active tabs (otherwise view-only). */
  editing: boolean;
}

/** Avatars shown before the rest collapse into a "+N" chip (fewer on narrow phones,
 *  where the header's right-hand cluster has to stay on one line). */
const MAX_SHOWN = 4;
const MAX_SHOWN_NARROW = 2;
const SIZE = 26;

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function roleLabel(v: ActiveViewer): string {
  return v.editing ? 'Editing' : 'Viewing';
}

function Avatar({ viewer, index }: { viewer: ActiveViewer; index: number }) {
  return (
    <span
      className="viewer-avatar"
      title={`${viewer.userName} · ${roleLabel(viewer)}`}
      style={{
        width: SIZE, height: SIZE, borderRadius: '50%',
        background: usernameColor(viewer.userName),
        border: '2px solid white',
        boxSizing: 'border-box',
        color: 'white', fontSize: 10, fontWeight: 700, letterSpacing: '0.2px',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        marginLeft: index === 0 ? 0 : -8,
        zIndex: MAX_SHOWN + 1 - index,
        position: 'relative',
        flexShrink: 0,
        userSelect: 'none',
      }}
    >
      {initials(viewer.userName)}
    </span>
  );
}

export function ActiveViewers({ viewers }: { viewers: ActiveViewer[] }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  // Close the list on a tap/click anywhere else.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', onDown);
    return () => document.removeEventListener('pointerdown', onDown);
  }, [open]);

  useEffect(() => {
    if (viewers.length === 0) setOpen(false);
  }, [viewers.length]);

  if (viewers.length === 0) return null;

  const narrow = window.matchMedia?.('(max-width: 400px)').matches ?? false;
  const shown = viewers.slice(0, narrow ? MAX_SHOWN_NARROW : MAX_SHOWN);
  const extra = viewers.length - shown.length;
  const summary = viewers.length === 1
    ? `${viewers[0].userName} is also here`
    : `${viewers.length} others are here`;

  return (
    <div ref={rootRef} style={{ position: 'relative', flexShrink: 0 }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-label={summary}
        aria-expanded={open}
        title={summary}
        style={{
          display: 'flex', alignItems: 'center', background: 'none', border: 'none',
          padding: 0, cursor: 'pointer',
        }}
      >
        {shown.map((v, i) => <Avatar key={v.userName} viewer={v} index={i} />)}
        {extra > 0 && (
          <span
            style={{
              height: SIZE, minWidth: SIZE, padding: '0 5px', borderRadius: SIZE / 2,
              background: 'rgba(255,255,255,0.25)', border: '2px solid white', boxSizing: 'border-box',
              color: 'white', fontSize: 10, fontWeight: 700,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              marginLeft: -8, position: 'relative', zIndex: 0,
            }}
          >
            +{extra}
          </span>
        )}
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="People in this session"
          style={{
            position: 'absolute', top: SIZE + 8, right: 0, zIndex: 1000,
            background: 'white', color: '#1f2937', borderRadius: 8,
            boxShadow: '0 6px 20px rgba(0,0,0,0.18)', padding: '6px 0',
            minWidth: 190, maxHeight: 320, overflowY: 'auto',
          }}
        >
          <div style={{ padding: '4px 12px 6px', fontSize: 11, fontWeight: 600, color: '#6b7280' }}>
            Also in this session
          </div>
          {viewers.map(v => (
            <div key={v.userName} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 12px' }}>
              <Avatar viewer={v} index={0} />
              <span style={{ fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                {v.userName}
              </span>
              <span style={{ fontSize: 11, color: v.editing ? '#047857' : '#6b7280' }}>{roleLabel(v)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
